import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Building2, Instagram, Linkedin, Youtube } from 'lucide-react';
import PillarStrip from './PillarStrip';

const TikTokIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

const XIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

// Two-tone text formatting component — no bold, lightweight
const ForwardsflowText = ({ variant = 'default' }) => (
  <span className="font-sans font-normal inline-block">
    <span className={variant === 'white' ? 'text-white' : 'text-black'}>Forwards</span>
    <span className={variant === 'white' ? 'text-zinc-300' : 'text-zinc-500'}>flow</span>
  </span>
);

const formatBrandText = (text, variant = 'default') => {
  if (typeof text !== 'string') return text;
  const tokens = [];
  let currentIdx = 0;
  const regex = /(Forwardsflow)/gi;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > currentIdx) {
      tokens.push(text.substring(currentIdx, match.index));
    }
    tokens.push(<ForwardsflowText key={match.index} variant={variant} />);
    currentIdx = regex.lastIndex;
  }

  if (currentIdx < text.length) {
    tokens.push(text.substring(currentIdx));
  }
  return tokens.length > 0 ? tokens : text;
};

const Footer = ({ ctaHeading, ctaDescription, hideCta } = {}) => {
  const socialLinks = [
    { href: 'https://www.instagram.com/forwardsflow/', icon: Instagram, label: 'Instagram' },
    { href: 'https://www.linkedin.com/company/forwards-flow/', icon: Linkedin, label: 'LinkedIn' },
    { href: 'https://www.tiktok.com/@forwardsflow', icon: TikTokIcon, label: 'TikTok' },
    { href: 'https://www.x.com/flowforwards', icon: XIcon, label: 'X' },
    { href: 'https://www.youtube.com/@forwardsflow', icon: Youtube, label: 'YouTube' },
  ];

  return (
    <footer className="bg-[#4D4D4D] text-white font-sans antialiased border-t border-[#4D4D4D]" role="contentinfo">
      {/* Footer CTA Block - (bg-[#5C5C5C]) */}
      {!hideCta && (
      <div className="bg-[#5C5C5C] text-white py-24 text-center">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-4xl mx-auto flex flex-col items-center">
            <h2 className="font-sans text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-6">{ctaHeading || 'Run your cross-border cashflows end to end.'}</h2>
            <p className={`font-sans text-sm sm:text-base text-zinc-300 mb-10 text-center ${ctaDescription ? '' : 'max-w-2xl'}`}>
              {ctaDescription || (<>
              Yield discovery, exchange rates, compliance fulfilment, hedging devices, liquidity preservation, transaction execution and portfolio management — one platform for cross-border cashflows across 6 markets.
              <br /><br />
              {formatBrandText("Open your account with Forwardsflow today.", "white")}
              </>)}
            </p>
            <div className="flex gap-4">
              <Link
                to="/apply"
                className="px-8 py-3.5 bg-white text-black font-mono text-xs uppercase tracking-[0.15em] font-semibold border border-white hover:bg-zinc-100 hover:border-zinc-100 transition-all"
              >
                Open Account
              </Link>
              <Link
                to="/demo"
                className="px-8 py-3.5 bg-transparent text-white font-mono text-xs uppercase tracking-[0.15em] font-semibold border border-white hover:bg-white hover:text-black transition-all"
              >
                Book a Demo
              </Link>
            </div>
          </div>
        </div>
      </div>
      )}


      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 mb-16">

          {/* Company Info */}
          <div className="lg:col-span-1 flex flex-col">
            <Link to="/" className="flex items-center gap-2.5 mb-6 hover:opacity-80 transition-opacity">
              <img src="/logo.svg" alt="Forwardsflow" loading="lazy" className="w-8 h-8 object-contain" />
              <span className="font-sans text-xl font-normal tracking-tight text-white">{formatBrandText("Forwardsflow", "white")}</span>
            </Link>
            <p className="text-xs font-mono uppercase tracking-[0.15em] text-zinc-300 mb-2">
              Full stack cashflow management.<sup className="text-[6px] sm:text-[8px]">™</sup>
            </p>
            <p className="text-xs text-zinc-300 font-sans mb-4 leading-relaxed">
              Yield discovery, exchange rates, compliance fulfilment, hedging devices, liquidity preservation, transaction execution and portfolio management across 6 markets.
            </p>
            <div className="mb-6">
              <PillarStrip variant="footer" />
            </div>
            {/* Supported Source Countries */}
            <div className="mb-4">
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400 block mb-2">Currently supported jurisdictions.</span>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <span className="w-6 h-4 overflow-hidden inline-block flex-shrink-0"><img src="/flags/jp.png" alt="Japan flag" loading="lazy" className="w-full h-full object-cover scale-[1.15]" /></span>
                  <span className="text-xs text-zinc-200 font-sans">Japan</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-6 h-4 overflow-hidden inline-block flex-shrink-0"><img src="/flags/ke.png" alt="Kenya flag" loading="lazy" className="w-full h-full object-cover scale-[1.15]" /></span>
                  <span className="text-xs text-zinc-200 font-sans">Kenya</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-4 h-4 overflow-hidden inline-block flex-shrink-0"><img src="/flags/ch.png" alt="Switzerland flag" loading="lazy" className="w-full h-full object-cover" /></span>
                  <span className="text-xs text-zinc-200 font-sans">Switzerland</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-6 h-4 overflow-hidden inline-block flex-shrink-0"><img src="/flags/ae.png" alt="UAE flag" loading="lazy" className="w-full h-full object-cover scale-[1.15]" /></span>
                  <span className="text-xs text-zinc-200 font-sans">UAE</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-6 h-4 overflow-hidden inline-block flex-shrink-0"><img src="/flags/gb.png" alt="United Kingdom flag" loading="lazy" className="w-full h-full object-cover scale-[1.15]" /></span>
                  <span className="text-xs text-zinc-200 font-sans">UK</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-6 h-4 overflow-hidden inline-block flex-shrink-0"><img src="/flags/us.png" alt="USA flag" loading="lazy" className="w-full h-full object-cover scale-[1.15]" /></span>
                  <span className="text-xs text-zinc-200 font-sans">USA</span>
                </div>
              </div>
            </div>

            <div className="flex gap-4 mb-6">
              <Link to="/apply" className="text-xs font-mono uppercase tracking-wider text-white underline hover:text-zinc-300">Open Account</Link>
              <span className="text-zinc-500">|</span>
              <Link to="/demo" className="text-xs font-mono uppercase tracking-wider text-white underline hover:text-zinc-300">Book a Demo</Link>
            </div>

            {/* Social Links — pushed to bottom to align with Global Presence UK box */}
            <div className="lg:mt-auto">
              <div className="flex items-center gap-2">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 border border-zinc-500 flex items-center justify-center text-zinc-200 hover:border-white hover:text-white transition-all bg-[#4D4D4D]"
                    aria-label={social.label}
                  >
                    <social.icon />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Contact Details */}
          <div id="contact">
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-300 mb-6">Contact Us</h3>
            <ul className="space-y-4 text-xs font-mono tracking-wider">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 text-zinc-300" />
                <span className="text-zinc-200 font-sans leading-relaxed text-xs">Forwards Flow Inc.<br />2055 Limestone Rd STE 200-C<br />Wilmington, DE 19808, New Castle<br />United States</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-zinc-300" />
                <a href="tel:+254785654887" className="hover:text-white transition-colors">+254 785 654 887</a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 mt-0.5 text-zinc-300" />
                <a href="mailto:info@forwardsflow.com" className="hover:text-white transition-colors">info@forwardsflow.com</a>
              </li>
              <li className="flex items-start gap-3">
                <Building2 className="w-4 h-4 mt-0.5 text-zinc-300" />
                <span className="text-zinc-200 font-sans leading-relaxed text-xs">Kofisi Square, 104 Riverside Drive, Nairobi, Kenya</span>
              </li>
            </ul>
          </div>

          {/* Quick Links */}
          <nav aria-label="Footer navigation">
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-300 mb-6">Quick Links</h3>
            <ul className="space-y-3 text-xs font-mono uppercase tracking-wider">
              <li><Link to="/" className="text-zinc-200 hover:text-white transition-colors">Home</Link></li>
              <li><Link to="/about" className="text-zinc-200 hover:text-white transition-colors">About Us</Link></li>
              <li><Link to="/apply" className="text-zinc-200 hover:text-white transition-colors">Open Account</Link></li>
              <li><Link to="/pricing" className="text-zinc-200 hover:text-white transition-colors">Pricing</Link></li>
              <li><Link to="/demo" className="text-zinc-200 hover:text-white transition-colors">Book a Demo</Link></li>
              <li><Link to="/privacy" className="text-zinc-200 hover:text-white transition-colors">Privacy Policy</Link></li>
              <li><Link to="/cookie-policy" className="text-zinc-200 hover:text-white transition-colors">Cookie Policy</Link></li>
              <li><Link to="/terms" className="text-zinc-200 hover:text-white transition-colors">Terms of Service</Link></li>
              <li><Link to="/contact" className="text-zinc-200 hover:text-white transition-colors">Contact</Link></li>
            </ul>
          </nav>

          {/* Global Presence */}
          <div>
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-300 mb-6">Global Presence</h3>
            <div className="flex flex-wrap gap-2 mb-6">
              {['London', 'Nairobi', 'Dubai'].map((city) => (
                <span key={city} className="px-3 py-1 border border-zinc-500 font-mono text-xs uppercase tracking-wider text-zinc-200">
                  {city}
                </span>
              ))}
            </div>
            <div className="space-y-3 text-xs font-mono">
              <div className="p-3 border border-zinc-500">
                <p className="text-zinc-300 uppercase">Headquarters</p>
                <p className="text-white mt-0.5">Kofisi Square, 104 Riverside Drive</p>
                <p className="text-zinc-300">Nairobi, Kenya</p>
              </div>
              <div className="p-3 border border-zinc-500">
                <p className="text-zinc-300 uppercase">Dubai</p>
                <p className="text-white mt-0.5">DIFC</p>
                <p className="text-zinc-300">Dubai, UAE</p>
              </div>
              <div className="p-3 border border-zinc-500">
                <p className="text-zinc-300 uppercase">United Kingdom</p>
                <p className="text-white mt-0.5">The City</p>
                <p className="text-zinc-300">London, UK</p>
              </div>
            </div>
          </div>

        </div>

        {/* Compliance Disclosure */}
        <div className="border-t border-zinc-500 pt-6 mt-8">
          <p className="text-[10px] text-zinc-400 font-sans leading-relaxed">
            Forwardsflow does not provide investment advice. Sovereign treasury bills carry sovereign credit risk, currency risk, and interest rate risk. Past yields are not indicative of future returns. Foreign exchange forwards contracts carry counterparty risk. Forwardsflow is not a broker-dealer, bank, or licensed investment advisor. T-bills are held in your securities depository account at the relevant national central depository.
          </p>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-zinc-500 pt-8 mt-8 flex flex-col md:flex-row items-center justify-between gap-4 font-mono text-[10px] text-zinc-300 uppercase tracking-widest">
          <p>&copy; {new Date().getFullYear()} {formatBrandText("Forwardsflow", "white")}. All rights reserved. WIPO utility patents pending. We defend IP.</p>
          <div className="flex gap-6">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/cookie-policy" className="hover:text-white transition-colors">Cookies</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <Link to="/disclaimer" className="hover:text-white transition-colors">Disclaimer</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
